import React, { useState, useEffect, useCallback } from 'react';
import styles from '../styles/contentReader.module.css';

interface ContentReaderProps {
  isOpen: boolean;
  onClose: () => void;
  filePath: string;
  startTag: string;
  endTag: string;
  title?: string;
}

interface ContentData {
  title: string;
  subtitle: string;
  content: string;
}

const ContentReader: React.FC<ContentReaderProps> = ({
  isOpen,
  onClose,
  filePath,
  startTag,
  endTag,
  title: propTitle
}) => {
  const [content, setContent] = useState<ContentData | null>(null);
  const [loading, setLoading] = useState(false);

  const loadContent = useCallback(async () => {
    if (!isOpen) return;
    
    setLoading(true);
    try {
      const response = await fetch(filePath);
      if (!response.ok) {
        throw new Error(`Failed to load content: ${response.statusText}`);
      }
      
      const text = await response.text();
      
      // Extract content between start and end tags
      let contentText;
      if (startTag === '' && endTag === '') {
        // If no tags specified, use the entire file content
        contentText = text.trim();
      } else {
        const startIndex = text.indexOf(startTag);
        const endIndex = text.indexOf(endTag);
        
        if (startIndex === -1 || endIndex === -1) {
          throw new Error('Content tags not found');
        }
        
        contentText = text.substring(startIndex + startTag.length, endIndex).trim();
      }
      
      // Extract title - first try the title tag, then fall back to first header
      const titleMatch = contentText.match(/<<title:\s*(.*?)>>/);
      let title = titleMatch ? titleMatch[1].trim() : 'Untitled';
      let firstHeaderMatch = null;
      
      // If no title tag found, look for the first header
      if (!titleMatch) {
        firstHeaderMatch = contentText.match(/^#\s+(.+)$/m);
        if (firstHeaderMatch) {
          title = firstHeaderMatch[1].trim();
        }
      }
      
      // Extract all subtitles
      const subtitleRegex = /<<subtitle:\s*(.*?)>>/g;
      const subtitles: string[] = [];
      let subtitleMatch;
      while ((subtitleMatch = subtitleRegex.exec(contentText)) !== null) {
        subtitles.push(subtitleMatch[1].trim());
      }
      
      // Format subtitles: first one bold italic, second one just italic on new line
      let subtitle = '';
      if (subtitles.length > 0) {
        subtitle = `<strong><em>${subtitles[0]}</em></strong>`;
        if (subtitles.length > 1) {
          subtitle += `<br><em>${subtitles[1]}</em>`;
        }
      }
      
      // Remove title and subtitle tags from content
      const cleanContent = contentText
        .replace(/<<title:\s*.*?>>/g, '')
        .replace(/<<subtitle:\s*.*?>>/g, '')
        .trim();
      

      
      setContent({
        title,
        subtitle,
        content: cleanContent
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setContent({
        title: 'Error',
        subtitle: '',
        content: 'Failed to load content. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  }, [isOpen, filePath, startTag, endTag]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const formatContent = (content: string): string => {
    // Convert markdown and HTML to basic HTML formatting
    let formatted = content
      .replace(/^### (.*$)/gim, '<h3>$1</h3>') // H3 headers
      .replace(/^## (.*$)/gim, '<h2>$1</h2>') // H2 headers
      .replace(/^# (.*$)/gim, '<h1>$1</h1>') // H1 headers
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>') // Links
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold markdown
      .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic markdown (asterisk)
      .replace(/_(.*?)_/g, '<em>$1</em>') // Italic markdown (underscore)
      .replace(/<b>(.*?)<\/b>/g, '<strong>$1</strong>') // HTML bold tags
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'); // Additional bold markdown (in case of nested)
    
    // Handle paragraph breaks more intelligently
    // First, normalize line breaks
    formatted = formatted.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    
    // Split into lines and process each line
    const contentLines = formatted.split('\n');
    const paragraphs: string[] = [];
    let currentParagraph = '';
    
    for (let i = 0; i < contentLines.length; i++) {
      const line = contentLines[i].trim();
      
      // If line is empty, it's a paragraph break
      if (line === '') {
        if (currentParagraph.trim()) {
          paragraphs.push(currentParagraph.trim());
          currentParagraph = '';
        }
      } else if (line.startsWith('<h') || line.startsWith('<table')) {
        // If we have a current paragraph, add it before the header/table
        if (currentParagraph.trim()) {
          paragraphs.push(currentParagraph.trim());
          currentParagraph = '';
        }
        // Add the header/table as its own element
        paragraphs.push(line);
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        // Handle bullet points (both dash and asterisk)
        if (currentParagraph.trim()) {
          paragraphs.push(currentParagraph.trim());
          currentParagraph = '';
        }
        // Convert bullet point to HTML list item
        const bulletContent = line.substring(2).trim(); // Remove the "- " or "* " prefix
        paragraphs.push(`<li>${bulletContent}</li>`);
      } else if (line.startsWith('> ')) {
        // Handle indented quotes
        if (currentParagraph.trim()) {
          paragraphs.push(currentParagraph.trim());
          currentParagraph = '';
        }
        // Convert quote to HTML blockquote
        const quoteContent = line.substring(2).trim(); // Remove the "> " prefix
        paragraphs.push(`<blockquote>${quoteContent}</blockquote>`);
      } else {
        // If we have content and this line starts with a capital letter (likely a new sentence/paragraph)
        // and the previous line ended with a period, treat as new paragraph
        if (currentParagraph && 
            line.match(/^[A-Z]/) && 
            currentParagraph.trim().endsWith('.')) {
          paragraphs.push(currentParagraph.trim());
          currentParagraph = line;
        } else {
          // Add to current paragraph
          if (currentParagraph) {
            currentParagraph += ' ' + line;
          } else {
            currentParagraph = line;
          }
        }
      }
    }
    
    // Add the last paragraph if it has content
    if (currentParagraph.trim()) {
      paragraphs.push(currentParagraph.trim());
    }
    
    // Join paragraphs with proper HTML - headers and tables don't need <p> tags
    // Also handle lists by grouping consecutive <li> elements
    let result = '';
    let inList = false;
    let listItems = [];
    
    for (let i = 0; i < paragraphs.length; i++) {
      const p = paragraphs[i];
      
      if (p.startsWith('<h') || p.startsWith('<table') || p.startsWith('<blockquote>')) {
        // If we were in a list, close it first
        if (inList) {
          result += `<ul>${listItems.join('')}</ul>`;
          listItems = [];
          inList = false;
        }
        result += p; // Headers, tables, and blockquotes are already HTML
      } else if (p.startsWith('<li>')) {
        // Start or continue a list
        if (!inList) {
          inList = true;
        }
        listItems.push(p);
      } else {
        // If we were in a list, close it first
        if (inList) {
          result += `<ul>${listItems.join('')}</ul>`;
          listItems = [];
          inList = false;
        }
        result += `<p>${p}</p>`; // Regular text gets paragraph tags
      }
    }
    
    // Close any remaining list
    if (inList) {
      result += `<ul>${listItems.join('')}</ul>`;
    }
    
    return result;
  };

  const handleOverlayClick = () => {
    onClose();
  };

  const formattedContent = content ? formatContent(content.content) : '';

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
          <h2 className={styles.title}>{propTitle || content?.title || 'Untitled'}</h2>
          <p className={styles.subtitle} dangerouslySetInnerHTML={{ __html: content?.subtitle || '' }} />
        </div>
        <div className={styles.content}>
          {loading ? (
            <div>Loading...</div>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: formattedContent }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentReader; 